import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { EctApiClient, localStorageStore, type Session } from "@ect/core";
import { primaryRole, type Role } from "@ect/contracts";
import { API_URL } from "./config";
import i18n from "../i18n";

interface SessionCtx { api: EctApiClient; session: Session | null; ready: boolean; role: Role; entityIds: string[]; chantierIds: string[] }
const Ctx = createContext<SessionCtx | null>(null);

/** Auth session held in the pluggable token store (localStorage here; Capacitor Preferences on phones). */
export function SessionProvider({ children }: { children: ReactNode }) {
  const api = useMemo(() => new EctApiClient(API_URL, localStorageStore()), []);
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void api.session().then((s) => { setSession(s); setReady(true); });
    return api.onSession(setSession);
  }, [api]);
  useEffect(() => {
    if (!session?.user.id) return;
    const saveLanguage = () => {
      const language = i18n.resolvedLanguage ?? "fr";
      void api.fetch("/api/auth/me", { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ preferred_lang: language }) }).catch(() => undefined);
    };
    saveLanguage();
    i18n.on("languageChanged", saveLanguage);
    window.addEventListener("online", saveLanguage);
    return () => { i18n.off("languageChanged", saveLanguage); window.removeEventListener("online", saveLanguage); };
  }, [api, session?.user.id]);
  const roles = session?.user.roles ?? [];
  const role = primaryRole(roles);
  const value = useMemo<SessionCtx>(() => ({ api, session, ready, role, entityIds: [...new Set(roles.map((r) => r.entity_id).filter((x): x is string => !!x))], chantierIds: [...new Set(roles.map((r) => r.chantier_id).filter((x): x is string => !!x))] }), [api, session, ready, role, roles]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("SessionProvider missing");
  return c;
}
